/*
 * Size Guide — transpose the chart at render + inches/centimeters toggle.
 *
 * TRANSPOSE
 * The size_chart metaobjects author their tables WIDE (sizes across the top as
 * column headers, measurements — Chest/Waist/Hip/Sleeve — down the side). That's
 * easy to read/enter in the admin but renders very wide on the storefront (heritage
 * charts hit 14 size columns). We flip it to TALL here: sizes become the left-hand
 * row headers and the measurements run across the top. ~4-5 columns instead of 14,
 * so it fits the foldout drawer without going dense or scrolling horizontally.
 *
 * UNITS
 * Charts are authored in inches. Every data cell that parses as a number, a range
 * ("33-34"), or a mixed fraction ("28 1/2") gets its inch text stored on the cell and a
 * centimeter version computed (x 2.54, one decimal). The IN / CM toggle in the drawer
 * header swaps the text; nothing is re-fetched. Cells that don't parse (e.g. "MT",
 * "One Size") are left alone. The choice is remembered in localStorage so it sticks
 * across products. Percent/tolerance columns headed "+/-" are converted too (a
 * tolerance of 1" is 2.5 cm).
 *
 * OVERFLOW (ADA Fix #052): see markOverflow() — mobile-only keyboard access to a table
 * wrap that genuinely scrolls sideways.
 *
 * Idempotent (guarded by data-transposed / data-units). ARIA scopes are rebuilt so
 * screen readers still get a correctly-associated header/data grid after the flip.
 */
(function () {
  var STORAGE_KEY = 'alpha-size-guide-unit';
  var IN_TO_CM = 2.54;

  function cellText(node) {
    return node ? node.innerHTML : '';
  }

  function transpose(table) {
    if (table.dataset.transposed === 'true') return;

    var thead = table.querySelector('thead');
    var tbody = table.querySelector('tbody');
    var headRow = thead && thead.querySelector('tr');
    if (!thead || !tbody || !headRow) return;

    var headCells = Array.prototype.slice.call(headRow.children);
    // headCells[0] is the empty corner; the rest are the size labels.
    var sizes = headCells.slice(1).map(cellText);

    var measures = Array.prototype.slice.call(tbody.querySelectorAll('tr')).map(function (tr) {
      var cells = Array.prototype.slice.call(tr.children);
      return { label: cellText(cells[0]), values: cells.slice(1).map(cellText) };
    });

    var newThead = document.createElement('thead');
    var htr = document.createElement('tr');
    var corner = document.createElement('th');
    corner.setAttribute('scope', 'col');
    // Empty corner cell fails axe "empty-table-header"; label it for screen readers only.
    corner.innerHTML = '<span class="visually-hidden">Size</span>';
    htr.appendChild(corner);
    measures.forEach(function (m) {
      var th = document.createElement('th');
      th.setAttribute('scope', 'col');
      th.innerHTML = m.label;
      htr.appendChild(th);
    });
    newThead.appendChild(htr);

    var newTbody = document.createElement('tbody');
    sizes.forEach(function (sizeLabel, i) {
      var tr = document.createElement('tr');
      var th = document.createElement('th');
      th.setAttribute('scope', 'row');
      th.innerHTML = sizeLabel;
      tr.appendChild(th);
      measures.forEach(function (m) {
        var td = document.createElement('td');
        td.innerHTML = m.values[i] !== undefined ? m.values[i] : '';
        tr.appendChild(td);
      });
      newTbody.appendChild(tr);
    });

    table.replaceChild(newThead, thead);
    table.replaceChild(newTbody, tbody);
    table.dataset.transposed = 'true';
  }

  /* ---------- unit conversion ---------- */

  // "28 1/2" -> 28.5, "28.5" -> 28.5, "28" -> 28, "3/4" -> 0.75. Returns NaN otherwise.
  var VULGAR = { '\u00bd': ' 1/2', '\u00bc': ' 1/4', '\u00be': ' 3/4', '\u215b': ' 1/8', '\u215c': ' 3/8', '\u215d': ' 5/8', '\u215e': ' 7/8' };

  function parseInches(str) {
    var s = String(str).replace(/[″"”]/g, '').replace(/[\u00bd\u00bc\u00be\u215b\u215c\u215d\u215e]/g, function (c) { return VULGAR[c]; }).replace(/\s+/g, ' ').trim();
    var m = s.match(/^(\d+)?(?:\s+(\d+)\/(\d+))?$/);
    if (m && (m[1] !== undefined || m[2] !== undefined)) {
      var whole = m[1] ? parseFloat(m[1]) : 0;
      var frac = m[2] ? parseFloat(m[2]) / parseFloat(m[3]) : 0;
      return whole + frac;
    }
    m = s.match(/^(\d+)\/(\d+)$/);
    if (m) return parseFloat(m[1]) / parseFloat(m[2]);
    if (/^\d+(\.\d+)?$/.test(s)) return parseFloat(s);
    return NaN;
  }

  function fmtCm(n) {
    var v = Math.round(n * IN_TO_CM * 10) / 10;
    return String(v).replace(/\.0$/, '');
  }

  // Converts a whole cell: single value, or a range "a-b" / "a – b" / "a to b".
  // Returns null when the cell isn't a measurement.
  function toCm(text) {
    var t = text.replace(/\s+/g, ' ').trim();
    if (!t) return null;
    var parts = t.split(/\s*(?:-|–|—|to)\s*/);
    if (parts.length > 2) return null;
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var n = parseInches(parts[i]);
      if (isNaN(n)) return null;
      out.push(fmtCm(n));
    }
    return out.join('-');
  }

  function prepareUnits(table) {
    if (table.dataset.units === 'ready') return;
    Array.prototype.forEach.call(table.querySelectorAll('tbody td'), function (td) {
      var inches = td.textContent;
      var cm = toCm(inches);
      if (cm === null) return;
      td.dataset.in = inches.trim();
      td.dataset.cm = cm;
    });
    table.dataset.units = 'ready';
  }

  function applyUnit(root, unit) {
    Array.prototype.forEach.call(root.querySelectorAll('.size-chart__table tbody td[data-cm]'), function (td) {
      td.textContent = unit === 'cm' ? td.dataset.cm : td.dataset.in;
    });
    Array.prototype.forEach.call(root.querySelectorAll('.size-guide__unit'), function (btn) {
      var on = btn.dataset.unit === unit;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    Array.prototype.forEach.call(root.querySelectorAll('.size-guide__unit-note'), function (el) {
      el.textContent = unit === 'cm' ? el.dataset.cmText : el.dataset.inText;
    });
    root.dataset.unit = unit;
  }

  function readStoredUnit() {
    try {
      var v = window.localStorage.getItem(STORAGE_KEY);
      return v === 'cm' ? 'cm' : 'in';
    } catch (e) {
      return 'in';
    }
  }

  function storeUnit(unit) {
    try {
      window.localStorage.setItem(STORAGE_KEY, unit);
    } catch (e) {
      /* private mode etc. — the toggle still works for this page */
    }
  }

  /*
   * ADA Fix #052 (WCAG 1.4.10 / 2.1.1): on narrow viewports the table wrap is the only
   * thing allowed to scroll sideways (CSS). A horizontally scrollable region must be
   * reachable by keyboard, so when — and only when — the table really overflows its
   * wrap, make the wrap focusable and name it after the table caption. Measured after
   * the drawer opens (it is display:none until then), after a unit switch (cm strings
   * are longer) and on resize. No extra tab stop when everything fits.
   */
  function markOverflow(guide) {
    Array.prototype.forEach.call(guide.querySelectorAll('.size-guide__table-wrap'), function (wrap) {
      var overflows = wrap.scrollWidth > wrap.clientWidth + 1;
      if (overflows) {
        if (!wrap.hasAttribute('tabindex')) {
          var caption = wrap.querySelector('caption');
          wrap.setAttribute('tabindex', '0');
          wrap.setAttribute('role', 'region');
          wrap.setAttribute('aria-label', (caption ? caption.textContent.trim() + ' ' : '') + 'size chart table, scrolls sideways');
        }
      } else if (wrap.hasAttribute('tabindex')) {
        wrap.removeAttribute('tabindex');
        wrap.removeAttribute('role');
        wrap.removeAttribute('aria-label');
      }
    });
  }

  function markAllOverflow() {
    Array.prototype.forEach.call(document.querySelectorAll('.size-guide'), markOverflow);
  }

  function initGuide(guide) {
    if (guide.dataset.sizeGuideReady === 'true') return;
    Array.prototype.forEach.call(guide.querySelectorAll('.size-chart__table'), function (table) {
      transpose(table);
      prepareUnits(table);
    });
    applyUnit(guide, readStoredUnit());
    guide.addEventListener('click', function (ev) {
      var btn = ev.target.closest && ev.target.closest('.size-guide__unit');
      if (btn) {
        var unit = btn.dataset.unit === 'cm' ? 'cm' : 'in';
        storeUnit(unit);
        // Keep every open guide on the page in step (quick-add drawers etc).
        Array.prototype.forEach.call(document.querySelectorAll('.size-guide'), function (g) {
          applyUnit(g, unit);
        });
      }
      // Trigger or unit button: the dialog is (about to be) open — measure on the next frame.
      window.requestAnimationFrame(function () { markOverflow(guide); });
    });
    var dialog = guide.querySelector('dialog');
    if (dialog && 'MutationObserver' in window) {
      new MutationObserver(function () {
        if (dialog.open) markOverflow(guide);
      }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
    guide.dataset.sizeGuideReady = 'true';
  }

  function run() {
    Array.prototype.forEach.call(document.querySelectorAll('.size-guide'), initGuide);
  }

  window.addEventListener('resize', markAllOverflow);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Expose for pages that render guides after load (section rerenders, quick add).
  window.AlphaSizeGuide = { init: run, toCm: toCm };
})();
